export type FieldType = 'condition' | 'condition_with_na' | 'yesno' | 'hvac_age' | 'plumbing_age';
export type SectionKey = 'hvac' | 'plumbing' | 'electrical' | 'water_treatment' | 'air_quality';

export interface AlertOption {
  value: string;
  label: string;
  color: string;
}

export const FIELD_OPTIONS: Record<FieldType, AlertOption[]> = {
  condition: [
    { value: 'good', label: 'Good', color: '#4caf50' },
    { value: 'needs_attention', label: 'Needs Attention', color: '#ff9800' },
    { value: 'requires_service', label: 'Requires Service', color: '#f44336' },
  ],
  condition_with_na: [
    { value: 'good', label: 'Good', color: '#4caf50' },
    { value: 'needs_attention', label: 'Needs Attention', color: '#ff9800' },
    { value: 'requires_service', label: 'Requires Service', color: '#f44336' },
    { value: 'n_a', label: 'None', color: '#9e9e9e' },
  ],
  yesno: [
    { value: 'yes', label: 'Yes', color: '#9e9e9e' },
    { value: 'no', label: 'No', color: '#9e9e9e' },
  ],
  hvac_age: [
    { value: '1_10', label: '1-10 Years', color: '#4caf50' },
    { value: '11_15', label: '11-15 Years', color: '#ff9800' },
    { value: '15_plus', label: '15+ Years', color: '#f44336' },
  ],
  plumbing_age: [
    { value: '1_6', label: '1-6 Years', color: '#4caf50' },
    { value: '6_9', label: '6-9 Years', color: '#ff9800' },
    { value: '10_15', label: '10-15 Years', color: '#f44336' },
  ],
};

export interface AttributeConfig {
  key: string;
  label: string;
  type: FieldType;
}

export interface SectionConfig {
  title: string;
  sectionKey: SectionKey;
  attributes: AttributeConfig[];
}

export const SECTIONS: SectionConfig[] = [
  {
    title: 'HVAC',
    sectionKey: 'hvac',
    attributes: [
      { key: 'hvac_age', label: 'Age', type: 'hvac_age' },
      { key: 'hvac_overall_visual_condition', label: 'Overall / Visual Condition', type: 'condition' },
      { key: 'hvac_overall_operation', label: 'Overall Operation', type: 'condition' },
      { key: 'hvac_boiler_ac_present', label: 'Air Conditioning', type: 'yesno' },
      { key: 'hvac_boiler_expansion_tank_condition', label: 'Expansion Tank', type: 'condition_with_na' },
      { key: 'hvac_boiler_heat_exchanger_condition', label: 'Heat Exchanger', type: 'condition_with_na' },
      { key: 'hvac_boiler_pressure_relief_condition', label: 'Pressure Relief', type: 'condition_with_na' },
      { key: 'hvac_boiler_auto_fill_condition', label: 'Auto-Fill', type: 'condition_with_na' },
      { key: 'hvac_proper_clearance', label: 'Proper Clearance', type: 'yesno' },
      { key: 'hvac_service_record_updated', label: 'Service Record Updated', type: 'yesno' },
    ],
  },
  {
    title: 'Plumbing',
    sectionKey: 'plumbing',
    attributes: [
      { key: 'plumbing_age', label: 'Age', type: 'plumbing_age' },
      { key: 'plumbing_overall_visual_condition', label: 'Overall / Visual Condition', type: 'condition' },
      { key: 'plumbing_expansion_tank_condition', label: 'Expansion Tank', type: 'condition' },
      { key: 'plumbing_visible_leaks_or_rust', label: 'Visible Leaks or Rust', type: 'yesno' },
      { key: 'plumbing_proper_clearance', label: 'Proper Clearance', type: 'yesno' },
      { key: 'plumbing_recommended_annual_maintenance', label: 'Recommended Annual Maintenance', type: 'yesno' },
      { key: 'plumbing_installed_coupon', label: 'Installed Coupon', type: 'yesno' },
      { key: 'plumbing_laundry_tray', label: 'Laundry Tray', type: 'condition_with_na' },
    ],
  },
  {
    title: 'Electrical',
    sectionKey: 'electrical',
    attributes: [
      { key: 'electrical_overall_visual_condition', label: 'Overall / Visual Condition', type: 'condition' },
      { key: 'electrical_whole_house_surge_protection', label: 'Whole House Surge Protection', type: 'condition' },
      {
        key: 'electrical_wiring_secured_properly_terminated',
        label: 'Wiring Secured / Properly Terminated',
        type: 'condition',
      },
      { key: 'electrical_whole_home_generator_condition', label: 'Whole Home Generator', type: 'condition' },
      { key: 'electrical_proper_clearance', label: 'Proper Clearance', type: 'yesno' },
    ],
  },
  {
    title: 'Indoor Air Quality',
    sectionKey: 'air_quality',
    attributes: [
      { key: 'air_quality_air_filter_condition', label: 'Air Filter Condition', type: 'condition' },
      { key: 'air_quality_iwave', label: 'iWave', type: 'condition_with_na' },
      { key: 'air_quality_humidifier', label: 'Humidifier', type: 'condition_with_na' },
      { key: 'air_quality_dehumidifier', label: 'Dehumidifier', type: 'condition_with_na' },
      { key: 'air_quality_programmable_thermostat', label: 'Programmable Thermostat', type: 'condition_with_na' },
    ],
  },
  {
    title: 'Water Treatment',
    sectionKey: 'water_treatment',
    attributes: [
      { key: 'water_treatment_visible_condition', label: 'Visible Condition of Equipment', type: 'condition' },
      { key: 'water_treatment_recommended_annual_maintenance', label: 'Recommended Annual Maintenance', type: 'yesno' },
      { key: 'water_treatment_filters_or_salt_required', label: 'Filters or Salt Required', type: 'yesno' },
      { key: 'water_treatment_water_quality_complaints', label: 'Water Quality Complaints', type: 'yesno' },
      { key: 'water_treatment_location_for_solution', label: 'Location for Solution', type: 'yesno' },
    ],
  },
];

export const ALL_KEYS = SECTIONS.flatMap((s) => s.attributes.map((a) => a.key));

export type AlertSettings = Record<string, string[]>;

export interface SectionRecipients {
  admin_user_ids: number[];
  salesperson_user_ids: number[];
  notify_salespeople: boolean;
}

export type RecipientsState = Record<SectionKey, SectionRecipients>;

export const DEFAULT_SECTION_RECIPIENTS: SectionRecipients = {
  admin_user_ids: [],
  salesperson_user_ids: [],
  notify_salespeople: false,
};

export const DEFAULT_RECIPIENTS: RecipientsState = {
  hvac: { ...DEFAULT_SECTION_RECIPIENTS },
  plumbing: { ...DEFAULT_SECTION_RECIPIENTS },
  electrical: { ...DEFAULT_SECTION_RECIPIENTS },
  water_treatment: { ...DEFAULT_SECTION_RECIPIENTS },
  air_quality: { ...DEFAULT_SECTION_RECIPIENTS },
};

export const DEFAULT_SETTINGS: AlertSettings = Object.fromEntries(ALL_KEYS.map((k) => [k, []]));

export interface UserOption {
  value: number;
  label: string;
}
